import React from 'react';
import { Link } from 'react-router-dom';
import { Leaf, Heart, Award, Users, ArrowRight } from 'lucide-react';

const AboutPage = () => {
  const values = [
    {
      icon: <Leaf className="w-8 h-8" />,
      title: 'Pure & Organic',
      description: 'We source only 100% natural and organic products with no artificial additives.'
    },
    {
      icon: <Heart className="w-8 h-8" />,
      title: 'Customer First',
      description: 'Your satisfaction is our priority. We ensure quality at every step.'
    },
    {
      icon: <Award className="w-8 h-8" />,
      title: 'Quality Assured',
      description: 'Every product is tested and certified for premium quality standards.'
    },
    {
      icon: <Users className="w-8 h-8" />,
      title: 'Community Support',
      description: 'We work directly with local farmers to support sustainable practices.'
    }
  ];

  return (
    <div className="min-h-screen bg-[#FFF8E7]" data-testid="about-page">
      {/* Header */}
      <div className="bg-white border-b border-[#6B4F2A]/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <nav className="flex items-center gap-2 text-sm text-[#574021] mb-4">
            <Link to="/" className="hover:text-[#1F7A63] transition-colors">Home</Link>
            <span className="text-[#6B4F2A]/30">/</span>
            <span className="text-[#2C1E0F] font-medium">About Us</span>
          </nav>
          <h1 className="text-3xl md:text-4xl font-black text-[#2C1E0F] tracking-tight" data-testid="about-title">
            About NatureWayz
          </h1>
        </div>
      </div>

      {/* Hero Section */}
      <section className="relative py-20" data-testid="about-hero">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage: `url('https://static.prod-images.emergentagent.com/jobs/764a0469-e026-4f18-9ad9-f58903c92cdd/images/09b093db05d5a3d7ab241e48e4ed0af1c8fd9e8acdff4e4096a8d3d742d0c5a0.png')`
          }}
        />
        <div className="absolute inset-0 bg-[#1F7A63]/85" />
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <span className="inline-block px-4 py-2 bg-[#E0A106] text-[#2C1E0F] rounded-full text-sm font-bold uppercase tracking-wider mb-6">
            Our Story
          </span>
          <h2 className="text-3xl md:text-5xl font-black text-white tracking-tight mb-6">
            Pure Nature, Delivered to You
          </h2>
          <p className="text-lg text-white/90 leading-relaxed max-w-2xl mx-auto">
            NatureWayz was born from a simple belief: everyone deserves access to pure, 
            organic products that nature intended. We bring the finest organic honey, 
            premium dates, nutritious seeds, and dry fruits directly from trusted farms to your doorstep.
          </p>
        </div>
      </section>

      {/* Mission Section */}
      <section className="section" data-testid="about-mission">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-xs uppercase tracking-[0.2em] font-bold text-[#1F7A63] mb-4 block">
                Our Mission
              </span>
              <h2 className="text-3xl md:text-4xl font-black text-[#2C1E0F] tracking-tight mb-6">
                Bringing Nature's Best to Pakistani Homes
              </h2>
              <p className="text-[#574021] leading-relaxed mb-6">
                At NatureWayz, we believe that what you put in your body matters. That's why we've 
                dedicated ourselves to sourcing the purest organic products from around the world 
                and making them accessible to everyone in Pakistan.
              </p>
              <p className="text-[#574021] leading-relaxed mb-8">
                We work directly with farmers and producers who share our commitment to quality 
                and sustainability. Every product goes through rigorous quality checks to ensure 
                it meets our high standards before reaching your home.
              </p>
              <Link to="/shop" className="btn-primary inline-flex items-center gap-2">
                Explore Our Products
                <ArrowRight className="w-5 h-5" />
              </Link>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <img
                src="https://images.unsplash.com/photo-1587049352846-4a222e784d38?w=400"
                alt="Pure Honey"
                className="rounded-2xl shadow-soft"
              />
              <img
                src="https://images.unsplash.com/photo-1596907844063-a5bf09f58d3f?w=400"
                alt="Premium Dates"
                className="rounded-2xl shadow-soft mt-8"
              />
              <img
                src="https://images.unsplash.com/photo-1604768802835-899055f0e245?w=400"
                alt="Organic Seeds"
                className="rounded-2xl shadow-soft"
              />
              <img
                src="https://images.unsplash.com/photo-1508061253366-f7da158b6d46?w=400"
                alt="Dry Fruits"
                className="rounded-2xl shadow-soft mt-8"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="section bg-white" data-testid="about-values">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <span className="text-xs uppercase tracking-[0.2em] font-bold text-[#1F7A63] mb-2 block">
              Our Values
            </span>
            <h2 className="text-3xl md:text-4xl font-black text-[#2C1E0F] tracking-tight">
              What Drives Us
            </h2>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, index) => (
              <div
                key={index}
                className="bg-[#FFF8E7] rounded-2xl p-8 text-center hover:shadow-soft transition-shadow"
                data-testid={`value-card-${index}`}
              >
                <div className="w-16 h-16 bg-[#1F7A63] rounded-2xl flex items-center justify-center mx-auto mb-4 text-white">
                  {value.icon}
                </div>
                <h3 className="font-bold text-[#2C1E0F] text-lg mb-2">{value.title}</h3>
                <p className="text-[#574021] text-sm">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-[#1F7A63]" data-testid="about-stats">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div>
              <p className="text-4xl md:text-5xl font-black text-[#E0A106]">5000+</p>
              <p className="text-white/80 mt-2">Happy Customers</p>
            </div>
            <div>
              <p className="text-4xl md:text-5xl font-black text-[#E0A106]">50+</p>
              <p className="text-white/80 mt-2">Products</p>
            </div>
            <div>
              <p className="text-4xl md:text-5xl font-black text-[#E0A106]">100%</p>
              <p className="text-white/80 mt-2">Organic</p>
            </div>
            <div>
              <p className="text-4xl md:text-5xl font-black text-[#E0A106]">4.9</p>
              <p className="text-white/80 mt-2">Average Rating</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section" data-testid="about-cta">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-black text-[#2C1E0F] tracking-tight mb-6">
            Ready to Experience Pure Nature?
          </h2>
          <p className="text-[#574021] text-lg mb-8">
            Join thousands of satisfied customers who trust NatureWayz for their organic products.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/shop" className="btn-primary inline-flex items-center justify-center gap-2">
              Shop Now
              <ArrowRight className="w-5 h-5" />
            </Link>
            <Link to="/contact" className="btn-outline inline-flex items-center justify-center">
              Contact Us
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
